import { useMemo } from "react";

import type { PlanDefault_EditPageQuery } from "@/lib/gql/__generated__/graphql";
import { SiteControllerType } from "@/lib/gql/__generated__/graphql";

import { serverModelToInputModelConverter } from "./converter/server-model-to-input-model/server-model-to-input-model";
import { useSendCustomMailSettingForCheckInBefore_00days } from "../../../../../../_shared/model/repository/mutation/check-in-before-00-days";
import { useSendCustomMailSettingForCheckInBefore_01days } from "../../../../../../_shared/model/repository/mutation/check-in-before-01-days";
import { useSendCustomMailSettingForCheckInBefore_07days } from "../../../../../../_shared/model/repository/mutation/check-in-before-07-days";
import { useSendCustomMailSettingForCheckOutAfter_01days } from "../../../../../../_shared/model/repository/mutation/check-out-after-01-days";
import { useSendCustomMailSettingForReservationCancel } from "../../../../../../_shared/model/repository/mutation/reservation-cancel";
import { useSendCustomMailSettingForReservationCreate } from "../../../../../../_shared/model/repository/mutation/reservation-create";
import { useSendCustomMailSettingForReservationUpdate } from "../../../../../../_shared/model/repository/mutation/reservation-update";
import { useInitProductPlanFormModelContext } from "../../../init";
import { usePlanEditMutation } from "../../../repository/existing/mutation/use-plan-edit-mutation";

import type { ProductPlanFormModel } from "../../..";
import type { ProductExistingPlanFormModelContextType } from "../../../../ui/existing/context/context";

type Props = {
	serverModel: PlanDefault_EditPageQuery;
};

export const useInitialized = ({ serverModel }: Props): ProductExistingPlanFormModelContextType => {
	const inputModel = useMemo(() => serverModelToInputModelConverter(serverModel), [serverModel]);

	const {
		hotel,
		hotelPolicy,
		customEmailSetting,
		roomList,
		optionList,
		discountList,
		plan,
		siteControllerSetting,
		planCustomMailSetting,
		basicCustomMailSetting,
	} = serverModel;

	const queryData = useMemo(() => {
		// 紐づいたまま非公開になった項目も一覧に残す
		// （一覧から消えると、ホストが紐づけを解除できなくなるため）
		const isLinkedDiscountID = (discountID: string) =>
			plan.availableDiscountIDs.includes(discountID) || plan.defaultDiscountID === discountID;
		const labelWithInSaleMark = (name: string, inSale: boolean) => (inSale ? name : `【非公開】${name}`);

		return {
			hotelEmailAddress: hotel.email ?? "",
			hotelPolicy: {
				checkInStart: hotelPolicy?.checkInStart ?? "",
				checkInEnd: hotelPolicy?.checkInEnd ?? "",
				checkOut: hotelPolicy?.checkOut ?? "",
				requiredPrepMillisecond: hotelPolicy?.requiredPrepMillisecond ?? 0,
				questions: hotelPolicy?.questions ?? [],
			},
			customEmailSetting: {
				reservation: {
					atCreated: customEmailSetting?.reservation?.atCreated ?? "",
					atUpdated: customEmailSetting?.reservation?.atUpdated ?? "",
					atCanceled: customEmailSetting?.reservation?.atCanceled ?? "",
					atCin: customEmailSetting?.reservation?.atCin ?? "",
				},
			},
			connection: {
				roomNameAndIDOptions: [
					...roomList.rooms.map((room) => {
						return {
							label: room.name,
							id: `${room.roomID}_${room.name}`,
							value: room.roomID,
						};
					}),
					...roomList.dummy.map((room) => {
						return {
							label: room.name,
							id: `${room.roomID}_${room.name}`,
							value: room.roomID,
						};
					}),
				],
				optionNameAndIDOptions: optionList.options
					.filter((option) => option.inSale || plan.availableOptionIDs.includes(option.optionID))
					.map((option) => {
						return {
							label: labelWithInSaleMark(option.name, option.inSale),
							id: `${option.optionID}_${option.name}`,
							value: option.optionID,
						};
					}),
				discountNameAndIDOptions: [
					...discountList.ordinary
						.filter((discount) => discount.inSale || isLinkedDiscountID(discount.discountID))
						.map((discount) => {
							return {
								label: labelWithInSaleMark(discount.name, discount.inSale),
								id: `${discount.discountID}_${discount.name}`,
								value: discount.discountID,
							};
						}),
					...discountList.longTerm
						.filter((discount) => discount.inSale || isLinkedDiscountID(discount.discountID))
						.map((discount) => {
							return {
								label: labelWithInSaleMark(discount.name, discount.inSale),
								id: `${discount.discountID}_${discount.name}`,
								value: discount.discountID,
							};
						}),
					...discountList.multi
						.filter((discount) => discount.inSale || isLinkedDiscountID(discount.discountID))
						.map((discount) => {
							return {
								label: labelWithInSaleMark(discount.name, discount.inSale),
								id: `${discount.discountID}_${discount.name}`,
								value: discount.discountID,
							};
						}),
				],
			},
			siteControllerSetting: {
				siteControllerType: siteControllerSetting?.siteControllerType ?? SiteControllerType.NotUsing,
				setUpRoomPriceByChillnn: siteControllerSetting?.setUpRoomPriceByChillnn ?? false,
			},
			forReservation: {
				atCreated: {
					text: planCustomMailSetting?.forReservation.atCreated.text ?? "",
					override: planCustomMailSetting?.forReservation.atCreated.override ?? false,
				},
				atUpdated: {
					text: planCustomMailSetting?.forReservation.atUpdated.text ?? "",
					override: planCustomMailSetting?.forReservation.atUpdated.override ?? false,
				},
				atCanceled: {
					text: planCustomMailSetting?.forReservation.atCanceled.text ?? "",
					override: planCustomMailSetting?.forReservation.atCanceled.override ?? false,
				},
				atCheckInBefore_07days: {
					text: planCustomMailSetting?.forReservation.atCheckInBefore_07days.text ?? "",
					shouldSendEmail:
						planCustomMailSetting?.forReservation.atCheckInBefore_07days.shouldSendEmail ?? false,
					override: planCustomMailSetting?.forReservation.atCheckInBefore_07days.override ?? false,
				},
				atCheckInBefore_01days: {
					text: planCustomMailSetting?.forReservation.atCheckInBefore_01days.text ?? "",
					shouldSendEmail:
						planCustomMailSetting?.forReservation.atCheckInBefore_01days.shouldSendEmail ?? false,
					override: planCustomMailSetting?.forReservation.atCheckInBefore_01days.override ?? false,
				},
				atCheckInBefore_00days: {
					text: planCustomMailSetting?.forReservation.atCheckInBefore_00days.text ?? "",
					shouldSendEmail:
						planCustomMailSetting?.forReservation.atCheckInBefore_00days.shouldSendEmail ?? false,
					override: planCustomMailSetting?.forReservation.atCheckInBefore_00days.override ?? false,
				},
				atCheckOutAfter_01days: {
					text: planCustomMailSetting?.forReservation.atCheckOutAfter_01days.text ?? "",
					shouldSendEmail:
						planCustomMailSetting?.forReservation.atCheckOutAfter_01days.shouldSendEmail ?? false,
					override: planCustomMailSetting?.forReservation.atCheckOutAfter_01days.override ?? false,
				},
			},
			// 基本メール設定を追加
			basicCustomMailSetting: basicCustomMailSetting?.forReservation,
		} satisfies ProductPlanFormModel["QueryData"];
	}, [
		customEmailSetting?.reservation?.atCanceled,
		customEmailSetting?.reservation?.atCin,
		customEmailSetting?.reservation?.atCreated,
		customEmailSetting?.reservation?.atUpdated,
		discountList,
		hotel.email,
		hotelPolicy?.checkInEnd,
		hotelPolicy?.checkInStart,
		hotelPolicy?.checkOut,
		hotelPolicy?.questions,
		hotelPolicy?.requiredPrepMillisecond,
		optionList.options,
		plan.availableDiscountIDs,
		plan.availableOptionIDs,
		plan.defaultDiscountID,
		roomList.dummy,
		roomList.rooms,
		siteControllerSetting?.setUpRoomPriceByChillnn,
		siteControllerSetting?.siteControllerType,
		planCustomMailSetting,
		basicCustomMailSetting,
	]);

	const { onSetViewModel, viewModel, resetViewModel, initInputModel } = useInitProductPlanFormModelContext({ inputModel, queryData });

	const { onUpdate } = usePlanEditMutation({ reset: resetViewModel });

	const { sendTestMailAtReservationCreate } = useSendCustomMailSettingForReservationCreate();
	const { sendTestMailAtReservationUpdate } = useSendCustomMailSettingForReservationUpdate();
	const { sendTestMailAtReservationCancel } = useSendCustomMailSettingForReservationCancel();
	const { sendTestMailAtCheckInBefore07Days } = useSendCustomMailSettingForCheckInBefore_07days();
	const { sendTestMailAtCheckInBefore01Days } = useSendCustomMailSettingForCheckInBefore_01days();
	const { sendTestMailAtCheckInBefore00Days } = useSendCustomMailSettingForCheckInBefore_00days();
	const { sendTestMailAtCheckOutAfter01Days } = useSendCustomMailSettingForCheckOutAfter_01days();

	return {
		queryData,
		onSetViewModel,
		resetViewModel,
		viewModel,
		initInputModel,
		onUpdate: () => onUpdate({ ...viewModel }),

		testEmailSender: {
			sendTestMailAtReservationCreate,
			sendTestMailAtReservationUpdate,
			sendTestMailAtReservationCancel,
			sendTestMailAtCheckInBefore07Days,
			sendTestMailAtCheckInBefore01Days,
			sendTestMailAtCheckInBefore00Days,
			sendTestMailAtCheckOutAfter01Days,
		},
	};
};
